/**
 * ConsentGate — the ESIGN §7001(c) consent step of the v2 ceremony (CB-5.3, FR-47/48).
 *
 * Renders the full §7001(c) disclosure (the legal-gated, versioned, tenant-aware slot), a
 * reasonable-demonstration-of-access gate (AccessDemonstration), and ONE affirmative control —
 * "I agree to use electronic records and signatures" — that is NOT pre-checked and stays disabled
 * until the document has been opened (mandatory sequence: open doc → agree → proceed). No marketing
 * CTA, decline never buried (UX-DR18/20). On accept it records consent via the token-scoped public
 * endpoint (consent_accepted_at + one consent_accepted event w/ timestamp/IP/version).
 *
 * ⚠️ The disclosure WORDING is counsel-gated (CB-1.10/OQ1). This renders the reserved placeholder
 * behind ESIGN_CONSENT_VERSION; a pending-legal notice is shown until ESIGN_CONSENT_APPROVED.
 */

import { useState } from 'react';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import { contractService } from '@/services/contractService';
import AccessDemonstration from './AccessDemonstration';
import {
  ESIGN_CONSENT_VERSION,
  ESIGN_CONSENT_APPROVED,
  ESIGN_AFFIRMATIVE_LABEL,
  buildEsignDisclosure,
} from './esignConsent';

interface ConsentGateProps {
  companyName: string;
  token: string;
  accent: string;
  ink: string;
  onConsented: () => void;
}

export default function ConsentGate({ companyName, token, accent, ink, onConsented }: ConsentGateProps) {
  const [accessDemonstrated, setAccessDemonstrated] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const disclosure = buildEsignDisclosure(companyName);

  async function handleContinue() {
    if (!agreed || !accessDemonstrated || submitting) return;
    setSubmitting(true);
    setError(null);
    const result = await contractService.recordConsentV2(token, ESIGN_CONSENT_VERSION);
    if (result.success) {
      onConsented();
      return;
    }
    setError(result.error || "We couldn't record your consent. Please try again.");
    setSubmitting(false);
  }

  return (
    <section className="mt-6" aria-labelledby="consent-heading">
      {/* Quiet, bordered consent card — NOT a marketing CTA (UX-DR18). */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 id="consent-heading" className="text-lg font-semibold text-slate-900">
          Before you sign
        </h2>
        <p className="mt-1 text-base leading-relaxed text-slate-600">
          Please review how this contract uses electronic records and signatures, then agree to
          continue.
        </p>

        {!ESIGN_CONSENT_APPROVED ? (
          <p
            className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800"
            role="note"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            This disclosure is a draft pending legal review. Exact §7001(c) wording will be finalized
            before launch.
          </p>
        ) : null}

        {/* Scrollable disclosure region — announced as a required step (NFR-14; full audit CB-5.11). */}
        <div
          className="mt-4 max-h-72 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-4"
          role="region"
          aria-label="Electronic records and signatures disclosure (required reading)"
          tabIndex={0}
        >
          <dl className="space-y-4">
            {disclosure.map((el) => (
              <div key={el.key}>
                <dt className="text-sm font-semibold text-slate-900">{el.heading}</dt>
                <dd className="mt-1 text-sm leading-relaxed text-slate-600">{el.body}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Reasonable demonstration of access — gates the affirmative control below. */}
        <AccessDemonstration
          token={token}
          satisfied={accessDemonstrated}
          onDemonstrated={() => setAccessDemonstrated(true)}
          accent={accent}
        />

        {/* The ONE affirmative control — not pre-checked; disabled until access is demonstrated. */}
        <label
          className={`mt-5 flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 focus-within:ring-2 focus-within:ring-offset-2 ${
            accessDemonstrated ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
          }`}
        >
          <input
            type="checkbox"
            // NFR-14/A11: the ESIGN consent is a REQUIRED step — announce it as such to screen readers.
            aria-required="true"
            className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300"
            style={{ accentColor: accent }}
            checked={agreed}
            disabled={!accessDemonstrated}
            onChange={(e) => {
              setAgreed(e.target.checked);
              setError(null);
            }}
          />
          <span className="text-base font-medium text-slate-900">{ESIGN_AFFIRMATIVE_LABEL}</span>
        </label>
        {!accessDemonstrated ? (
          <p className="mt-2 text-xs text-slate-500">Open the document above to enable this.</p>
        ) : null}

        {error ? (
          <p className="mt-3 text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <button
        type="button"
        onClick={handleContinue}
        disabled={!agreed || !accessDemonstrated || submitting}
        className="mt-5 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl px-4 text-base font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        style={{ backgroundColor: accent, color: ink }}
      >
        <ShieldCheck className="h-5 w-5" aria-hidden="true" />
        {submitting ? 'Saving…' : 'Agree & continue'}
      </button>
    </section>
  );
}
